import React, { useState } from 'react';
import AddModal from '../../Resume_Submission/Components/AddModal';
import BASE_URL from '../../url';
import Swal from 'sweetalert2';
import { CheckCircle2, KeyRound, Mail, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function PublicBenchCandidateModal({ isOpen, onClose, initialData }) {
  const [saving, setSaving] = useState(false);
  const [successInfo, setSuccessInfo] = useState(null);
  const navigate = useNavigate();

  if (!isOpen) return null;

  // Prepare initial candidate data from the Job Application
  const rawPhone = initialData?.mobile || initialData?.Mobile || initialData?.phone || initialData?.Phone || '';
  const candidateInitData = {
    FirstName: initialData?.first_name || initialData?.FirstName || '',
    LastName: initialData?.last_name || initialData?.LastName || '',
    MiddleName: initialData?.middle_name || initialData?.MiddleName || '',
    Email: initialData?.email || initialData?.Email || '',
    Mobile: rawPhone,
    Phone: '',
    JobTitle: initialData?.position || initialData?.JobTitle || '',
    WorkAuthorization: initialData?.visa_type || initialData?.WorkAuthorization || '',
    EmploymentType: initialData?.employment_type || initialData?.EmploymentType || '',
    Country: initialData?.country || initialData?.Country || 'US',
    State: initialData?.current_state || initialData?.State || '',
    City: initialData?.current_city || initialData?.City || '',
    CandidateStatus: 'Available',
    IsBench: true,
    WillingToRelocate: true,
    ResumeFile: initialData?.resumeFile || initialData?.file || null,
  };

  const handleSaveBenchCandidate = async (formData) => {
    try {
      setSaving(true);

      const dataToSend = new FormData();
      dataToSend.append('FirstName', formData.firstName || formData.FirstName || '');
      dataToSend.append('LastName', formData.lastName || formData.LastName || '');
      dataToSend.append('MiddleName', formData.middleName || formData.MiddleName || '');
      dataToSend.append('EmailID', (formData.email || formData.Email || '').trim());
      dataToSend.append('Phone', (formData.phone || formData.Phone || '').trim());
      dataToSend.append('Mobile', (formData.mobile || formData.Mobile || '').trim());
      dataToSend.append('JobTitle', formData.jobTitle || formData.JobTitle || '');
      dataToSend.append('YearsOfExperience', formData.yearsOfExperience ?? formData.YearsOfExperience ?? '');
      dataToSend.append('CandidateStatus', formData.candidateStatus || 'Available');
      dataToSend.append('RemoteStatus', formData.remoteStatus || 'Remote');
      dataToSend.append('WorkAuthorization', formData.workAuthorization || '');
      dataToSend.append('EmploymentType', formData.employmentType || '');
      dataToSend.append('SecurityClearance', formData.securityClearance || '');
      dataToSend.append('WillingToRelocate', formData.willingToRelocate ? 'true' : 'false');
      dataToSend.append('IsBench', 'true');
      dataToSend.append('Gender', formData.gender || '');
      dataToSend.append('Country', formData.country || formData.Country || '');
      dataToSend.append('State', formData.state || formData.State || '');
      dataToSend.append('City', formData.city || formData.City || '');
      dataToSend.append('CurrentLocation', formData.currentLocation || '');
      dataToSend.append('ProfileSummary', formData.profileSummary || '');
      dataToSend.append('LinkedInProfile', formData.linkedInUrl || formData.LinkedInUrl || '');
      dataToSend.append('GitHubUrl', formData.gitHubUrl || '');
      dataToSend.append('TwitterUrl', formData.twitterUrl || '');
      dataToSend.append('VideoResumeUrl', formData.videoResumeUrl || '');

      // Skills
      const skillsList = (formData.skills || []).map(s => typeof s === 'string' ? s : (s.SkillName || s.skillName || '')).filter(Boolean);
      dataToSend.append('Skills', JSON.stringify(skillsList));

      // Work Experience
      const workList = (formData.workExperience || []).map(w => ({
        Company: (w.company || w.Company || '').trim(),
        JobTitle: (w.jobTitle || w.JobTitle || '').trim(),
        StartDate: w.startDate || w.StartDate || '',
        EndDate: w.endDate || w.EndDate || '',
        IsCurrent: w.isCurrent ?? w.IsCurrent ?? false,
        Description: (w.description || w.Description || '').trim()
      })).filter(w => w.Company || w.JobTitle);
      dataToSend.append('WorkExperience', JSON.stringify(workList));

      // Education
      const eduList = (formData.education || []).map(e => ({
        Institution: (e.institution || e.Institution || '').trim(),
        Degree: (e.degree || e.Degree || '').trim(),
        FieldOfStudy: (e.fieldOfStudy || e.FieldOfStudy || '').trim(),
        StartDate: e.startDate || e.StartDate || '',
        EndDate: e.endDate || e.EndDate || '',
        GPA: (e.gpa || e.GPA || '').trim()
      })).filter(e => e.Institution || e.Degree);
      dataToSend.append('Education', JSON.stringify(eduList));

      // Resume file
      if (formData.ResumeFile) {
        dataToSend.append('resume', formData.ResumeFile);
      } else if (initialData?.resumeFile) {
        dataToSend.append('resume', initialData.resumeFile);
      }

      // Additional Documents
      if (formData.documents && Array.isArray(formData.documents)) {
        const docsMeta = [];
        formData.documents.forEach((doc, idx) => {
          if (doc.file) {
            const fieldName = `document_file_${idx}`;
            dataToSend.append(fieldName, doc.file);
            docsMeta.push({
              fieldName,
              documentName: doc.documentName || doc.file.name,
              documentType: doc.documentType || 'OTHER'
            });
          }
        });
        if (docsMeta.length > 0) {
          dataToSend.append('documents_meta', JSON.stringify(docsMeta));
        }
      }

      const response = await fetch(`${BASE_URL}/api/ats/bench-apply`, {
        method: 'POST',
        body: dataToSend
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Failed to submit bench candidate application');
      }

      setSuccessInfo(result);
    } catch (err) {
      console.error('❌ Bench registration error:', err);
      Swal.fire({
        icon: 'error',
        title: 'Submission Error',
        text: err.message || 'Could not complete bench enrollment. Please try again.',
        confirmButtonColor: '#3b5bfd'
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {!successInfo ? (
        <AddModal
          candidate={candidateInitData}
          onClose={onClose}
          onSave={handleSaveBenchCandidate}
          uploading={saving}
        />
      ) : (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(15, 23, 42, 0.65)',
          backdropFilter: 'blur(6px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '20px'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '16px',
            maxWidth: '540px',
            width: '100%',
            padding: '36px 32px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            textAlign: 'center',
            animation: 'fadeInScale 0.3s ease-out'
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: '#dcfce7',
              color: '#3b5bfd',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px'
            }}>
              <CheckCircle2 size={36} />
            </div>

            <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#0f172a', margin: '0 0 8px' }}>
              Enrolled in Bench Candidates!
            </h2>
            <p style={{ fontSize: '14px', color: '#64748b', margin: '0 0 24px', lineHeight: '1.5' }}>
              Your profile is now active on our bench network. You will be prioritized for immediate client requisitions.
            </p>

            <div style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '10px',
              padding: '18px 20px',
              marginBottom: '24px',
              textAlign: 'left'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontSize: '14px' }}>
                <span style={{ color: '#64748b' }}>Candidate ID:</span>
                <span style={{ fontWeight: '700', color: '#3b5bfd', letterSpacing: '0.5px' }}>{successInfo.candidateCode || 'CAN-BENCH'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontSize: '14px' }}>
                <span style={{ color: '#64748b' }}>Registered Email:</span>
                <span style={{ fontWeight: '600', color: '#1e293b' }}>{successInfo.email || candidateInitData.Email}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px', paddingTop: '12px', borderTop: '1px dashed #cbd5e1', fontSize: '13px', color: '#0369a1' }}>
                <Mail size={16} />
                <span>Login credentials & status link emailed to your inbox.</span>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={() => {
                  onClose();
                  navigate(`/track-status?q=${encodeURIComponent(successInfo.candidateCode || successInfo.email || '')}`);
                }}
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  background: 'linear-gradient(135deg, #3b5bfd 0%, #1d4ed8 100%)',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '12px 20px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 4px 6px -1px rgba(13, 148, 136, 0.3)'
                }}
              >
                <span>Track Application</span>
                <ArrowRight size={18} />
              </button>

              <button
                onClick={() => {
                  onClose();
                  navigate('/login');
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  background: '#f1f5f9',
                  color: '#334155',
                  border: '1px solid #cbd5e1',
                  borderRadius: '10px',
                  padding: '12px 20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                <KeyRound size={16} />
                <span>Login</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
